import type { ReactNode } from 'react'

import { upperFirst } from 'lodash'
import { useIntl } from 'react-intl'

import type { Insurance, InsuranceComparison } from '@subaio/api-types/service'
import { ButtonGroup, FormattedMessage } from '@subaio/components'

import { ButtonText } from 'components/Buttons'
import { IconCheckPlain, IconX } from 'components/Icons'
import { PolicyItem } from 'components/Lists'

import { Title } from '../../Title'
import { PolicyList } from './PolicyList'
import { PolicyText } from './PolicyText'
import { PolicyTextItem } from './PolicyTextItem'
import { PolicyWrapper } from './PolicyWrapper'

type Props = {
	title: ReactNode
	coverage: InsuranceComparison['policies'][0]['coverage']
	insurances: Array<Insurance>

	onClose: () => void
}

export const Modal = ({ title, coverage, insurances, onClose }: Props) => {
	const { formatMessage } = useIntl()
	const coverageSummary = coverage.map(({ title, coveredBy }) => {
		const type = coveredBy.some(({ description }) => description !== null) ? 'text' : 'boolean'

		return {
			title,
			type,
			insurances: insurances.map(({ title }) => {
				const insuranceThatCovers = coveredBy.find(({ title: titleCovered }) => title === titleCovered)
				return {
					title,
					isCovered: Boolean(insuranceThatCovers),
					description: insuranceThatCovers?.description,
				}
			}),
		}
	})

	return (
		<>
			<Title>{title}</Title>
			<PolicyList>
				{coverageSummary.map(({ title, insurances, type }, index) => (
					<PolicyWrapper key={index}>
						<b>{upperFirst(title)}</b>
						{insurances.map(({ title, isCovered, description }, index) => {
							switch (type) {
								case 'text':
									return (
										<PolicyTextItem key={index}>
											<p role='text'>{title}</p>
											<PolicyText>
												{description ?? (
													<span
														role='text'
														aria-label={formatMessage({
															id: 'termsAndConditions.modal.descriptionMissing',
															defaultMessage: 'Not included',
														})}
													>
														-
													</span>
												)}
											</PolicyText>
										</PolicyTextItem>
									)
								case 'boolean':
									return (
										<PolicyItem
											key={index}
											isCovered={isCovered}
											role='text'
											aria-label={`${
												isCovered
													? formatMessage({ id: 'termsAndConditions.modal.included', defaultMessage: 'Included' })
													: formatMessage({
															id: 'termsAndConditions.modal.notIncluded',
															defaultMessage: 'Not included',
														})
											}. ${title}`}
										>
											{isCovered ? <IconCheckPlain aria-hidden /> : <IconX aria-hidden />}
											<span aria-hidden>{title}</span>
										</PolicyItem>
									)
								default:
									return null
							}
						})}
					</PolicyWrapper>
				))}
			</PolicyList>
			<ButtonGroup center>
				<ButtonText
					label={<FormattedMessage id='termsAndConditions.modal.buttonClose' defaultMessage='Close' />}
					onClick={onClose}
				/>
			</ButtonGroup>
		</>
	)
}
